/**
 * The unit price a catalogue article brings to a document line, in the
 * document's price mode (SFE §6.2, §6.7, §6.10).
 *
 * An article carries its price in its own mode (`Article.priceMode`); an
 * invoice, a quote or a till cart has one price mode for all its lines. The
 * article's price goes onto a line converted into the document's mode: one
 * unit of the article is computed in its own mode by `calculateLineTax`, with
 * its tax group, TSB and specific tax, and the line takes that unit's TTC
 * (the levies and their VAT included) on a TTC document, its HT on an HT
 * document. The conversion is per unit, to the centime: a line of several
 * units can differ by centimes from the same quantity computed in the
 * article's own mode.
 *
 * @module @efacture/fiscal-core/services/catalogue-price
 */
import { TAX_GROUPS } from "../constants/tax-groups.js";
import { specificTaxParamsFromFields } from "./specific-tax.js";
import { calculateLineTax } from "./tax-calculator.js";
function isPriceMode(value) {
    return value === "HT" || value === "TTC";
}
function numberOf(value) {
    if (typeof value === "number")
        return value;
    if (typeof value === "string")
        return Number(value);
    return value.toNumber();
}
/**
 * The unit price of `article` on a line of a document in `documentMode`.
 * Equal to the article's price when the modes match. The article's own
 * price is computed in every case, so an article whose price the tax
 * engine refuses (unknown mode or tax group, price not above zero, a
 * specific tax reaching the TTC price) throws its French refusal here.
 */
export function catalogueUnitPrice(article, documentMode) {
    if (!isPriceMode(documentMode)) {
        throw new Error(`Mode de prix du document « ${documentMode} » inconnu (HT ou TTC)`);
    }
    if (!isPriceMode(article.priceMode)) {
        throw new Error(`Mode de prix de l'article « ${article.priceMode} » inconnu (HT ou TTC)`);
    }
    if (!(article.taxGroup in TAX_GROUPS)) {
        throw new Error(`Groupe de taxe invalide: ${article.taxGroup}`);
    }
    const unitPrice = numberOf(article.unitPrice);
    if (!Number.isFinite(unitPrice)) {
        throw new Error(`Prix de l'article invalide: ${String(article.unitPrice)}`);
    }
    const unit = calculateLineTax({
        unitPrice,
        quantity: 1,
        discount: 0,
        taxGroup: article.taxGroup,
        isBeverage: article.isBeverage ?? false,
        specificTax: specificTaxParamsFromFields(article),
        priceMode: article.priceMode,
    });
    if (documentMode === article.priceMode)
        return unitPrice;
    return documentMode === "TTC" ? unit.lineTotalTTC : unit.netHT;
}
//# sourceMappingURL=catalogue-price.js.map